import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { useAppStore } from 'context/AppStore';
import { billingAPI } from '../../api/client';

const NAV = [
  { to: '/',          icon: '◎', label: 'Dashboard',      exact: true },
  { to: '/publish',   icon: '⚡', label: 'Publicar',       highlight: true },
  //{ to: '/bulk',      icon: '🚀', label: 'Publicar en masa' },
  { to: '/ads',       icon: '💰', label: 'Campañas de pago' },
  { to: '/crm', icon: '🏢', label: 'CRM' },
  { to: '/reservations', icon: '🗓', label: 'Reservas' },
  { to: '/products',   icon: '📦', label: 'Productos' },
  { to: '/billing',   icon: '💳', label: 'Mi plan' },
  { to: '/posts',     icon: '◫', label: 'Publicaciones' },
  { to: '/calendar',  icon: '📅', label: 'Calendario' },
  { to: '/analytics', icon: '◈', label: 'Analytics' },
  { to: '/projects',  icon: '◧', label: 'Proyectos' },
  { to: '/accounts',  icon: '◉', label: 'Cuentas' },
  { to: '/media',     icon: '🖼', label: 'Biblioteca' },
  { to: '/automation', icon: '⚙',  label: 'Automatización' },
];

// Plan minimo requerido por ruta (null/ausente = sin restriccion).
// TENANT_ROUTE_MIN_PLAN: heredan el plan de la empresa (compartido con el equipo).
// INDIVIDUAL_ROUTE_MIN_PLAN: exigen la suscripcion PROPIA del usuario, sin heredar de nadie.
const PLAN_LEVELS = { free: 0, starter: 1, pro: 2, agency: 3 };
const TENANT_ROUTE_MIN_PLAN = {
  '/crm': 'starter',
  '/products': 'starter',
  '/reservations': 'pro',
};
const INDIVIDUAL_ROUTE_MIN_PLAN = {
  '/analytics': 'starter',
  '/ads': 'pro',
  '/automation': 'agency',
};

const ADMIN_ONLY_NAV = [
  { to: '/providers',     icon: '◬', label: 'Proveedores IA' },
  { to: '/settings',      icon: '◎', label: 'Configuración' },
  { to: '/billing-admin', icon: '💵', label: 'Billing Admin' },
];

const ADMIN_NAV = [
  { to: '/users', icon: '◑', label: 'Usuarios' },
];

export default function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const isOwner = user?.is_platform_owner;
  const { sidebarOpen, notifications } = useAppStore();
  const navigate = useNavigate();
  const [planSlug, setPlanSlug] = useState(null);
  const [individualPlanSlug, setIndividualPlanSlug] = useState(null);

  useEffect(() => {
    billingAPI.effectivePlan().then(r => setPlanSlug(r.data?.slug || null)).catch(() => {});
    billingAPI.individualPlan().then(r => setIndividualPlanSlug(r.data?.slug || null)).catch(() => {});
  }, []);

  const isLocked = (to) => {
    const tenantMinPlan = TENANT_ROUTE_MIN_PLAN[to];
    if (tenantMinPlan) {
      const userLevel = PLAN_LEVELS[planSlug] ?? 0;
      return userLevel < PLAN_LEVELS[tenantMinPlan];
    }
    const individualMinPlan = INDIVIDUAL_ROUTE_MIN_PLAN[to];
    if (individualMinPlan) {
      const userLevel = PLAN_LEVELS[individualPlanSlug] ?? 0;
      return userLevel < PLAN_LEVELS[individualMinPlan];
    }
    return false;
  };

  if (!sidebarOpen) return null;

  return (
    <aside style={{
      width: 'var(--sidebar-width)',
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      background: 'var(--bg-surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 200,
      overflowY: 'auto',
      overflowX: 'hidden',
      // Safe area izquierda (notch landscape)
      paddingLeft: 'env(safe-area-inset-left)',
    }}>

      {/* Logo */}
      <div style={{
        padding: '20px 16px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0,
      }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '9px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden', background: 'transparent',
        }}>
          <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '15px', lineHeight: 1 }}>TX-Flow AI</div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Social Automation</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {NAV.map(item => {
          const locked = isLocked(item.to);
          if (locked) {
            return (
              <div
                key={item.to}
                onClick={() => navigate('/pricing')}
                title="Disponible en un plan superior"
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: 'var(--radius-md)',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                  color: 'var(--text-muted)', opacity: 0.55,
                  border: '1px solid transparent', minHeight: '44px',
                }}
              >
                <span style={{ fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={{ fontSize: '13px' }}>🔒</span>
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: 'var(--radius-md)',
                textDecoration: 'none', fontSize: '14px', fontWeight: '500',
                transition: 'var(--transition)',
                background: isActive
                  ? (item.highlight ? 'var(--gradient-primary)' : 'var(--bg-hover)')
                  : item.highlight ? 'var(--gradient-glow)' : 'transparent',
                color: isActive
                  ? (item.highlight ? '#fff' : 'var(--text-primary)')
                  : item.highlight ? '#a78bfa' : 'var(--text-secondary)',
                border: item.highlight && !isActive ? '1px solid #7c3aed40' : '1px solid transparent',
                minHeight: '44px',
              })}
            >
              <span style={{ fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.to === '/publish' && (
                <span style={{ fontSize: '9px', background: '#a78bfa', color: '#000', padding: '2px 6px', borderRadius: '999px', fontWeight: '800' }}>NEW</span>
              )}
            </NavLink>
          );
        })}

        {/* Rutas solo admin */}
        {isAdmin && ADMIN_ONLY_NAV.map(item => (
          <NavLink
            key={item.to} to={item.to}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 12px', borderRadius: 'var(--radius-md)',
              textDecoration: 'none', fontSize: '14px', fontWeight: '500',
              transition: 'var(--transition)',
              background: isActive ? 'var(--bg-hover)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: '1px solid transparent',
              minHeight: '44px',
            })}
          >
            <span style={{ fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div style={{ margin: '8px 12px 4px', fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Admin</div>
            {ADMIN_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: 'var(--radius-md)',
                  textDecoration: 'none', fontSize: '14px', fontWeight: '500',
                  transition: 'var(--transition)',
                  background: isActive ? 'var(--bg-hover)' : 'transparent',
                  color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                  border: '1px solid transparent',
                  minHeight: '44px',
                })}
              >
                <span style={{ fontSize: '16px', width: '20px', textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
        {isOwner && (
          <NavLink to="/platform-settings" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 12px', borderRadius: 'var(--radius-md)',
            textDecoration: 'none', fontSize: '14px', fontWeight: '500',
            background: isActive ? 'var(--bg-hover)' : 'transparent',
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            border: '1px solid transparent', minHeight: '44px',
          })}>
            <span style={{ fontSize: '16px', width: '20px', textAlign: 'center' }}>🛠</span>
            <span>Plataforma</span>
          </NavLink>
        )}
      </nav>

      {/* User */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 12px', borderRadius: 'var(--radius-md)',
          background: 'var(--bg-elevated)',
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0,
          }}>
            {user?.name?.charAt(0) || 'A'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.role}</div>
          </div>
          <button
            onClick={logout}
            title="Cerrar sesión"
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px', padding: '4px', borderRadius: '6px', minHeight: '44px', minWidth: '44px' }}
          >⏻</button>
        </div>
      </div>
    </aside>
  );
}
